import { useMemo, useState } from 'react';
import { Modal } from '@/components/ui/modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import {
  createScheduledReport,
  updateScheduledReport,
  type ScheduledReportDto,
} from '../api/reports-api';
import { REPORT_TYPE_LABELS } from '@/features/properties/components/scheduled-report-labels';

// Mirrors the backend default-subject template. Keep in sync with
// ScheduledReportSender on the API side: property-scoped reports include
// the property name; org-wide reports do not.
function buildDefaultSubject(reportType: string, propertyName?: string): string {
  const humanReportType = REPORT_TYPE_LABELS[reportType] ?? reportType;
  const trimmedName = propertyName?.trim();
  return trimmedName
    ? `Your manor ${humanReportType} Report — ${trimmedName}`
    : `Your manor ${humanReportType} Report`;
}

interface ScheduleReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyId: string;
  propertyName: string;
  userEmail?: string;
  // When provided, the modal switches into edit mode and submits a PUT
  // against the existing schedule. When omitted, behavior is unchanged
  // (create mode).
  editingSchedule?: ScheduledReportDto;
  // Fired on successful create/update so the parent can refresh its list
  // without re-fetching on every modal open.
  onSaved?: (schedule: ScheduledReportDto) => void;
}

export function ScheduleReportModal({
  isOpen,
  onClose,
  propertyId,
  propertyName,
  editingSchedule,
  onSaved,
}: ScheduleReportModalProps) {
  const isEdit = !!editingSchedule;
  const reportType = editingSchedule?.reportType ?? 'UsageSummary';
  const [format, setFormat] = useState(editingSchedule?.format ?? 'Csv');
  const [frequency, setFrequency] = useState(editingSchedule?.frequency ?? 'weekly');
  const [email, setEmail] = useState(editingSchedule?.deliveryEmail ?? '');

  // Combined "order by" key — collapses sortBy + sortDir into one Select
  // entry (`unit-asc`, `unit-desc`, `serialNumber-asc`, `serialNumber-desc`)
  // so the user picks one option instead of two coupled dropdowns. Default
  // matches the device list's default sort: unit ascending. Schedules
  // created before this feature shipped have null sortBy/sortDir; the
  // backend applies the same defaults at render time, so we mirror them
  // in the picker.
  const [orderKey, setOrderKey] = useState(() => {
    const sb = editingSchedule?.sortBy ?? 'unit';
    const sd = editingSchedule?.sortDir ?? 'asc';
    return `${sb}-${sd}`;
  });
  // Empty string = no grouping (flat list, current behavior). 'building'
  // partitions the report by building with section headers per group.
  const [groupBy, setGroupBy] = useState<string>(editingSchedule?.groupBy ?? '');

  // Default subject is derived from the (immutable-in-this-modal) reportType
  // and propertyName, so memoising keeps the submit-time comparison stable
  // against re-renders.
  const defaultSubject = useMemo(
    () => buildDefaultSubject(reportType, propertyName),
    [reportType, propertyName],
  );

  // In edit mode, fall back to the default when the persisted subject is
  // null/blank — that's the same signal the backend uses to mean "use the
  // template", so the field shows the user what'll actually go out.
  const initialSubject =
    editingSchedule && editingSchedule.subject && editingSchedule.subject.trim().length > 0
      ? editingSchedule.subject
      : defaultSubject;
  const [subject, setSubject] = useState(initialSubject);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  async function handleSubmit() {
    if (!email.trim()) {
      setError('Email is required');
      return;
    }
    setLoading(true);
    setError('');
    try {
      // Resolve subject to send: keep the data store clean by sending null
      // whenever the user is implicitly opting into the template (cleared
      // field, or the field still matches the computed default verbatim).
      // This way a property rename later still flows into the next email.
      const trimmedSubject = subject.trim();
      const subjectToSend =
        trimmedSubject.length === 0 || trimmedSubject === defaultSubject.trim()
          ? null
          : subject;

      // Split the combined order key back into the two backend fields.
      // The format is `{sortBy}-{sortDir}` and orderKey is always one of
      // the four canonical values, so the destructure is safe.
      const [sortBy, sortDir] = orderKey.split('-') as [string, string];
      // Empty groupBy maps to null on the wire so the server stores the
      // "no grouping" intent rather than a literal empty string.
      const groupByToSend = groupBy.length === 0 ? null : groupBy;

      let saved: ScheduledReportDto;
      if (isEdit && editingSchedule) {
        saved = await updateScheduledReport(editingSchedule.id, {
          reportType,
          format,
          frequency,
          deliveryEmail: email.trim(),
          // Preserve current active flag — pause/resume is handled elsewhere.
          isActive: editingSchedule.isActive,
          subject: subjectToSend,
          sortBy,
          sortDir,
          groupBy: groupByToSend,
        });
      } else {
        saved = await createScheduledReport({
          reportType,
          format,
          propertyId,
          frequency,
          deliveryEmail: email.trim(),
          subject: subjectToSend,
          sortBy,
          sortDir,
          groupBy: groupByToSend,
        });
      }
      onSaved?.(saved);
      setSuccess(true);
      setTimeout(() => { onClose(); setSuccess(false); }, 1500);
    } catch (err: unknown) {
      const fallback = isEdit ? 'Failed to update schedule.' : 'Failed to create schedule.';
      setError(
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? fallback,
      );
    } finally {
      setLoading(false);
    }
  }

  const title = isEdit ? 'Edit Scheduled Report' : 'Schedule Report';
  const submitLabel = isEdit ? 'Save Changes' : 'Create Schedule';
  const successMessage = isEdit ? 'Schedule updated!' : 'Schedule created!';

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title} size="md">
      <div className="space-y-4">
        {success ? (
          <div className="text-center py-6">
            <p className="text-lg font-medium text-success-600">{successMessage}</p>
            <p className="text-sm text-gray-500 mt-1">Reports will be emailed to {email}.</p>
          </div>
        ) : (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Property</label>
              <p className="text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-700 rounded-lg px-3 py-2">{propertyName}</p>
            </div>

            <Select
              label="Format"
              value={format}
              onChange={setFormat}
              options={[
                { value: 'Csv', label: 'CSV (Excel)' },
                { value: 'Pdf', label: 'PDF' },
              ]}
            />

            <Select
              label="Frequency"
              value={frequency}
              onChange={setFrequency}
              options={[
                { value: 'daily', label: 'Daily' },
                { value: 'weekly', label: 'Weekly (Monday)' },
                { value: 'monthly', label: 'Monthly (1st)' },
              ]}
            />

            <Select
              label="Order by"
              value={orderKey}
              onChange={setOrderKey}
              options={[
                { value: 'unit-asc', label: 'Unit number (low → high)' },
                { value: 'unit-desc', label: 'Unit number (high → low)' },
                { value: 'serialNumber-asc', label: 'Serial number (A → Z)' },
                { value: 'serialNumber-desc', label: 'Serial number (Z → A)' },
              ]}
            />

            <Select
              label="Group by"
              value={groupBy}
              onChange={setGroupBy}
              options={[
                { value: '', label: 'None (flat list)' },
                { value: 'building', label: 'Building' },
              ]}
            />

            <Input
              label="Email subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={defaultSubject}
              helperText="Leave as-is to use the default. Property name and report type are filled in for you."
              data-testid="schedule-report-subject-input"
            />

            <Input
              label="Delivery Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              data-testid="schedule-report-email-input"
              required
            />

            {error && <p className="text-sm text-error-600">{error}</p>}

            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={onClose} data-testid="schedule-report-cancel-button">Cancel</Button>
              <Button onClick={handleSubmit} isLoading={loading} data-testid="schedule-report-submit-button">
                {submitLabel}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
